import _ from "lodash";
import React, { memo } from "react";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import classes from "./Header.scss";
import {
  PrintOutlined,
  StarsTwoFilled,
  DotsVerticalOutlined,
  DashboardOutlined,
  ChatOutlined,
  EyeOutlined,
} from "@toddle-edu/ds-icons";
import {
  Button,
  IconButton,
  Dropdown,
  DropdownMenu,
  Badge,
  Tooltip,
  PageHeader,
} from "@toddle-edu/ds-web";
import AssessmentIcon from "UIComponents/AssessmentIcon";
import AIStarButton from "UIComponents/AIStarButton";
import NewProductUpdatesCallout from "UIComponents/NewProductUpdatesCallout";
import AssignmentEvaluationAICallout from "AppComponents/AssignmentEvaluationAICallout";
import I18nHOC from "UIComponents/I18nHOC";
import {
  getIsLessonPlanningEnabled,
  getIsAIGradingEnabled,
} from "ClassRoom/modules/utils";
import {
  ASSESSMENT,
  ASSESSMENT_TYPE_AI_TUTOR,
  ASSESSMENT_TYPE_WORKSHEET,
  ASSIGNMENT_TYPE_QUICK_TASK,
  TASK_TYPE_WORKSHEET,
  TASK_TYPE_AI_TUTOR,
  TASK_TYPE_QUICK_TASK,
} from "Constants/stringConstants";
import ACLStore from "lib/aclStore";
import {
  getIsAITutorModalitiesEnabled,
  getIsAssessmentTypeConfigurationEnabled,
} from "Utils/ACLStoreUtils";
import { getAssessmentObj } from "ClassRoom/modules/ClassRoomModule";
import { isAIModuleMarketingEnabled } from "Platform/modules/PlatformUtils";
import { AI_MODULES_FOR_MARKETING } from "ChildrenPage/components/AIModuleMarketingModal/AIModuleMarketingUtils";
import { setAiModuleMarketingKey } from "Platform/modules/PlatformModule";
import AITutorChatResetButton from "AppComponents/AIComponents/AITutorChatResetButton";
import AITutorSegmentControl from "AppComponents/AIComponents/AITutorSegmentControl";

const getDropdownOptions = params => {
  const { t, showPlannerOverview, isUnitSummaryActive } = params;

  const plannerOverviewLabel = isUnitSummaryActive
    ? `${t("common:hide_with_label", {
        label: t("common:planner_overview"),
      })}`
    : `${t("common:show_with_label", {
        label: t("common:planner_overview"),
      })}`;

  const options = [
    {
      key: "show_planner_overview",
      label: plannerOverviewLabel,
      icon: <DashboardOutlined />,
      perm:
        ACLStore.can("TeacherPortal:ShowUnitSummaryInLe") &&
        showPlannerOverview,
    },
    {
      key: "print",
      label: t("common:print"),
      icon: <PrintOutlined />,
      perm: !ACLStore.can("FeatureFlag:PreventPrintAndScreenshots"),
    },
  ];

  return _.filter(
    options,
    option => _.get(option, "perm") || !_.has(option, "perm")
  );
};

const AI_GRADING_VIDEO_URL =
  "https://iframe.dacast.com/vod/ea71159a-bfba-7a06-500f-93b65dfdcdf5/cc2b2dd8-c0cd-4dca-845e-23d798ff692d";

const AI_TUTOR_LEARNING_TOOLS_VIDEO_URL =
  "https://iframe.dacast.com/vod/ea71159a-bfba-7a06-500f-93b65dfdcdf5/beb7ef78-fa94-4a0b-bbc1-340685c04848";

const Header = memo(props => {
  const {
    t,
    onClose,
    totalCommentCount,
    unreadCommentCount,
    onCommentClick,
    onUnitSummaryClick,
    onClickPrint,
    curriculumType,
    toggleAssignmentAIChat,
    assessmentType,
    taskTypeGroup,
    isCommentActive,
    mode,
    getAssessmentObj,
    showSaveButton = false,
    onClickSave,
    toggleAITutorStudentPreviewModal,
    testId = "libraryCommonComponent",
    assessmentId,
    curriculumId,
    isGeneratingAIResponse,
  } = props;

  const isAITutor =
    taskTypeGroup === TASK_TYPE_AI_TUTOR ||
    assessmentType === ASSESSMENT_TYPE_AI_TUTOR;

  const typeObj = getAssessmentObj({
    curriculumType,
    assessmentValue: assessmentType,
    contentType: ASSESSMENT,
    curriculumId,
  });

  const { typeLabel, typeLabel2 } = typeObj || {};

  const renderComment = () => {
    const ButtonComponent = () => {
      return (
        <Button
          variant={"neutral"}
          type={"outlined"}
          size={"medium"}
          dsVersion={"2.0"}
          icon={<ChatOutlined />}
          isActivated={isCommentActive}
          onClick={onCommentClick}
          testId={`${testId}-header-chat`}
        >
          {totalCommentCount}
        </Button>
      );
    };
    if (totalCommentCount > 0) {
      if (unreadCommentCount > 0) {
        return (
          <Badge
            dsVersion={"2.0"}
            placement="topLeft"
            type="dot"
            offset={[8, 12]}
          >
            <ButtonComponent />
          </Badge>
        );
      } else {
        return <ButtonComponent />;
      }
    } else {
      return (
        <IconButton
          icon={<ChatOutlined />}
          dsVersion={"2.0"}
          size={"medium"}
          variant={"neutral"}
          type={"outlined"}
          isActivated={isCommentActive}
          onClick={onCommentClick}
        />
      );
    }
  };

  const onDropdownItemClick = ({ key }) => {
    switch (key) {
      case "show_planner_overview": {
        onUnitSummaryClick();
        break;
      }
      case "print": {
        onClickPrint();
        break;
      }
    }
  };

  const onAIMarketingClick = () => {
    props.setAiModuleMarketingKey(AI_MODULES_FOR_MARKETING.LESSON_PLANNING);
  };

  const options = getDropdownOptions(props);

  const showAIIcon = getIsLessonPlanningEnabled(assessmentType, taskTypeGroup);
  const isAssessmentTypeConfigurationEnabled = getIsAssessmentTypeConfigurationEnabled();

  const showMarketingAIIcon =
    !showAIIcon &&
    !_.includes(
      [
        ASSESSMENT_TYPE_AI_TUTOR,
        ASSESSMENT_TYPE_WORKSHEET,
        ASSIGNMENT_TYPE_QUICK_TASK,
      ],
      assessmentType
    ) &&
    (!isAssessmentTypeConfigurationEnabled ||
      !_.includes(
        [TASK_TYPE_AI_TUTOR, TASK_TYPE_WORKSHEET, TASK_TYPE_QUICK_TASK],
        taskTypeGroup
      )) &&
    props.isAIModuleMarketingEnabled(AI_MODULES_FOR_MARKETING.LESSON_PLANNING);

  const headerLabel =
    mode === "create"
      ? assessmentType === ASSESSMENT_TYPE_AI_TUTOR ||
        taskTypeGroup === TASK_TYPE_AI_TUTOR
        ? "common:create_an_with_label"
        : "common:create_a_with_label"
      : "common:edit_with_label";

  const title = isAssessmentTypeConfigurationEnabled
    ? typeLabel
    : t(headerLabel, {
        label: t(typeLabel2 || typeLabel),
      });

  const showAIGradingCallout =
    getIsAIGradingEnabled() &&
    !_.includes(
      [
        ASSESSMENT_TYPE_AI_TUTOR,
        ASSESSMENT_TYPE_WORKSHEET,
        ASSIGNMENT_TYPE_QUICK_TASK,
      ],
      assessmentType
    ) &&
    (!isAssessmentTypeConfigurationEnabled ||
      !_.includes(
        [TASK_TYPE_AI_TUTOR, TASK_TYPE_WORKSHEET, TASK_TYPE_QUICK_TASK],
        taskTypeGroup
      ));

  return (
    <PageHeader dsVersion={"2.0"}>
      <PageHeader.Left
        onBackClick={onClose}
        title={title}
        icon={
          <AssessmentIcon
            assessmentType={assessmentType}
            iconSize={"medium"}
            curriculumId={curriculumId}
          />
        }
      />

      {isAITutor && getIsAITutorModalitiesEnabled() ? (
        <PageHeader.Middle>
          <AITutorSegmentControl assessmentId={assessmentId} />
        </PageHeader.Middle>
      ) : null}

      <PageHeader.Right>
        {isAITutor && getIsAITutorModalitiesEnabled() ? (
          <NewProductUpdatesCallout
            uid="AI_TUTOR"
            videoUrl={AI_TUTOR_LEARNING_TOOLS_VIDEO_URL}
            title={t("ai:call_out_heading")}
            description={t("ai:call_out_description")}
            customDescriptionClass="text-body"
          />
        ) : null}
        <AITutorChatResetButton
          assessmentId={assessmentId}
          isGeneratingAIResponse={isGeneratingAIResponse}
        />

        {/* AI tutor student preview */}
        {assessmentType === ASSESSMENT_TYPE_AI_TUTOR ||
        taskTypeGroup === TASK_TYPE_AI_TUTOR ? (
          <Tooltip
            dsVersion={"2.0"}
            showArrow={true}
            tooltip={t("common:student_preview")}
          >
            {getIsAITutorModalitiesEnabled() ? (
              <Button
                testId={`${testId}-student-preview`}
                variant={"neutral"}
                type={"outlined"}
                dsVersion={"2.0"}
                onClick={toggleAITutorStudentPreviewModal}
                icon={<EyeOutlined />}
              >
                {t("common:student_preview")}
              </Button>
            ) : (
              <IconButton
                testId={`${testId}-student-preview`}
                variant={"neutral"}
                type={"outlined"}
                dsVersion={"2.0"}
                onClick={toggleAITutorStudentPreviewModal}
                icon={<EyeOutlined />}
              />
            )}
          </Tooltip>
        ) : null}

        {showAIGradingCallout ? (
          <AssignmentEvaluationAICallout
            title={t("ai:discover_the_ai_grading_assistant")}
            message={t("ai:discover_the_ai_grading_assistant_message")}
            videoUrl={AI_GRADING_VIDEO_URL}
          />
        ) : null}

        {/* Save button */}
        {showSaveButton ? (
          <Button
            testId={`${testId}-header-save`}
            variant="progressive"
            dsVersion={"2.0"}
            onClick={onClickSave}
          >
            {t("common:save")}
          </Button>
        ) : null}

        {/* AI Icon */}
        {showAIIcon ? (
          <div
            role={"button"}
            className={classes.assistantAvatar}
            onClick={toggleAssignmentAIChat}
            tabIndex={0}
            aria-label={"Open AI Assistant"}
            onKeyDown={e =>
              (e.key === "Enter" || e.key === " ") && toggleAssignmentAIChat()
            }
          >
            <StarsTwoFilled variant={"link"} />
          </div>
        ) : null}

        {showMarketingAIIcon ? (
          <AIStarButton onClick={onAIMarketingClick} />
        ) : null}

        {/* Comment */}
        {renderComment()}

        <div className={classes.separator} />

        {/* Planner overview, print */}
        <Dropdown
          overlay={
            <DropdownMenu
              options={options}
              onClick={onDropdownItemClick}
              testId={`${testId}-header-actionMenu`}
              shouldStopPropagation={true}
              dsVersion={"2.0"}
            />
          }
          dsVersion={"2.0"}
          placement={"bottomRight"}
        >
          <IconButton
            icon={<DotsVerticalOutlined />}
            testId={`${testId}-header-actionMenu`}
            dsVersion={"2.0"}
            size={"medium"}
            variant={"neutral"}
            type={"plain"}
          />
        </Dropdown>
      </PageHeader.Right>
    </PageHeader>
  );
});

Header.displayName = "Header";

const mapActionCreators = {
  getAssessmentObj,
  setAiModuleMarketingKey,
  isAIModuleMarketingEnabled,
};

export default compose(
  I18nHOC({ resource: "common" }),
  connect(null, mapActionCreators)
)(Header);
